import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { MsalService } from '@azure/msal-angular';
import { TranslocoModule } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { Client } from '../../../api/app-types/client.app';
import { DisabilityProof } from '../../../api/app-types/disability-proof.app';
import { DisabilityKind } from '../../../api/app-types/disability-type.app';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { ClientResolved } from '../../../shared/routing/resolvers/client.resolver';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { LocalStorageService } from '../../../shared/storage/local-storage.service';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { DisabilityProofList } from './disability-proof-list/disability-proof-list';
import { DisabilityTypeList } from './disability-type-list/disability-type-list';

interface ClientDisabilityPageSizeState {
	proofs: number;
	types: number;
}

const clientDisabilityPageSizes = signal<Record<number, ClientDisabilityPageSizeState>>({});

@Component({
	selector: 'lvr-client-disabilities',
	templateUrl: './client-disabilities.html',
	imports: [TranslocoModule, DisabilityTypeList, DisabilityProofList, LvrButton, LvrPaddingDirective],
	styleUrl: './client-disabilities.scss',
})
export class ClientDisabilities extends RouteInjection {
	private readonly session = inject(SessionManagerService);
	private readonly env = inject(ENVIRONMENT);
	private readonly auth = inject(MsalService, { optional: true });
	private readonly storage = inject(LocalStorageService, { optional: true });
	private readonly data = toSignal(this.route.data);
	private readonly clientResolved = this.resolveDataAsSignal<ClientResolved>('client');
	public readonly client = computed<Client | undefined>(() => {
		const c = this.clientResolved();
		return c?.kind === 'full' ? c.value : undefined;
	});

	public readonly proofPageIndex = signal(0);
	public readonly proofPageSize = signal(this.env.defaultPageSize);
	public readonly typePageIndex = signal(0);
	public readonly typePageSize = signal(this.env.defaultPageSize);
	public readonly showFirstLastButtons = true;
	public readonly hidePageSize = false;

	public readonly proofs = computed(() => {
		const data = this.data()!['proofs'] as DisabilityProof[];
		return data.sort((a, b) => b.since.getTime() - a.since.getTime());
	});
	public readonly disabilityKinds = computed(() => {
		const data = this.data()!['disabilityKinds'] as PaginatedResponse<DisabilityKind>;
		return data.entities;
	});
	public readonly proofTotalItems = computed(() => this.proofs().length);
	public readonly typeTotalItems = computed(() => this.disabilityKinds().length);

	public readonly paginatedProofs = computed(() => {
		const start = this.proofPageIndex() * this.proofPageSize();
		const end = start + this.proofPageSize();
		return this.proofs().slice(start, end);
	});

	public readonly paginatedDisabilityKinds = computed(() => {
		const start = this.typePageIndex() * this.typePageSize();
		const end = start + this.typePageSize();
		return this.disabilityKinds().slice(start, end);
	});

	private readonly lastRestoredClientId = signal<number | undefined>(undefined);

	private readonly restorePaginationOnClientChange = effect(() => {
		const currentClientId = this.client()?.id;
		if (currentClientId == null) return;
		if (this.lastRestoredClientId() === currentClientId) return;

		const savedPageSizes = untracked(() => clientDisabilityPageSizes()[currentClientId]);
		const savedProofsFromStorage = this.getSavedPageSize('disability-proof-list');
		const savedTypesFromStorage = this.getSavedPageSize('disability-type-list');
		untracked(() => {
			this.proofPageIndex.set(0);
			this.typePageIndex.set(0);
			this.proofPageSize.set(savedProofsFromStorage ?? savedPageSizes?.proofs ?? this.env.defaultPageSize);
			this.typePageSize.set(savedTypesFromStorage ?? savedPageSizes?.types ?? this.env.defaultPageSize);
			this.lastRestoredClientId.set(currentClientId);
		});
	});

	public readonly hasClientsEditPermission = hasPermission(this.session.permissions, [
		'Clients.Edit',
		'Clients.Edit.PhaseOneOnly',
	]);
	public readonly disableAddProofButton = computed(() => this.proofs().length >= this.env.maxDisabilityProofsPerClient);
	public readonly disableAddTypeButton = computed(
		() => this.disabilityKinds().length >= this.env.maxDisabilityTypesPerClient,
	);

	public handleProofPageEvent(event: PageEvent): void {
		this.proofPageIndex.set(event.pageIndex);
		this.proofPageSize.set(event.pageSize);
		this.persistPageSizeForCurrentClient('proofs', event.pageSize);
		this.savePageSize('disability-proof-list', event.pageSize);
	}

	public handleTypePageEvent(event: PageEvent): void {
		this.typePageIndex.set(event.pageIndex);
		this.typePageSize.set(event.pageSize);
		this.persistPageSizeForCurrentClient('types', event.pageSize);
		this.savePageSize('disability-type-list', event.pageSize);
	}

	public openCreateProof(): void {
		this.router.navigate(['proofs', 'create'], { relativeTo: this.route });
	}

	public openCreateType(): void {
		this.router.navigate(['type', 'add'], { relativeTo: this.route });
	}

	private persistPageSizeForCurrentClient(type: keyof ClientDisabilityPageSizeState, value: number): void {
		const currentClientId = this.client()?.id;
		if (currentClientId == null) return;

		const allPageSizes = clientDisabilityPageSizes();
		const currentState =
			allPageSizes[currentClientId] ??
			({
				proofs: this.env.defaultPageSize,
				types: this.env.defaultPageSize,
			} as ClientDisabilityPageSizeState);

		clientDisabilityPageSizes.set({
			...allPageSizes,
			[currentClientId]: {
				...currentState,
				[type]: value,
			},
		});
	}

	private getCurrentUserId(): string | undefined {
		return this.storage?.resolveCurrentUserId(this.auth);
	}

	private getSavedPageSize(preferenceKey: string): number | undefined {
		const userId = this.getCurrentUserId();
		if (!userId || !this.storage) return undefined;

		const saved = this.storage.getPageSize(userId, preferenceKey);
		if (!saved || !this.env.pageSizeOptions.includes(saved)) return undefined;
		return saved;
	}

	private savePageSize(preferenceKey: string, pageSize: number): void {
		const userId = this.getCurrentUserId();
		if (!userId || !this.storage) return;
		this.storage.setPageSize(userId, preferenceKey, pageSize);
	}
}
